/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import ArgosButton from "../../Buttons/ArgosButton"
import ArgosDialogHeader from "../ArgosDialogHeader"
import ArgosFlexbox from "../../Containers/ArgosFlexbox"
import ArgosMenuItem from "../../Form/Select/ArgosMenuItem"
import ArgosSelect from "../../Form/Select/ArgosSelect"
import ArgosTextField from "../../Form/TextField/ArgosTextField"
import {config} from "../../../../AppConfig/config"
import {Container} from "../../../pages/CoverageRequirements/Table/constants"
import {FormControl, IconButton, InputAdornment, InputLabel, Typography} from "@mui/material"
import GlobalFrame from "../../../images/coordinate_system/Global_Frame.png";
import {Header} from "../../../pages/CoverageRequirements/Table/Dialogs/constants"
import {loginRequest, requestIdToken} from "../../../../auth/authConfig"
import {ReferenceFrame, axisTransformationOptions} from "../../../pages/vehiclePrograms/ItemDetails/constants"
import {setCoordinateSystemName, setIsConfigSensorReferenceFrameDialogOpen} from "../../../../redux/ducks/VehicleProgramsDetails/slice"
import {useAppDispatch} from "../../../../redux/hooks"
import {useMsal} from "@azure/msal-react"
import {useState} from "react"
import VectorExample from "../../../images/coordinate_system/Vector_Example.png";
import {VEHICLE_PROGRAMS_CONSTANTS} from "../../../pages/vehiclePrograms/constants"
import {isEqual} from "lodash"
import CloseIcon from '@mui/icons-material/Close';


interface ConfigSensorReferenceFrameDialogProps {
  vehicleProgramId: string
  developmentStageId: string
  globalReferenceFrame: any
  setGlobalReferenceFrame: any
  localReferenceFrame: any
  setLocalReferenceFrame: any
  originTransformation: any
  setOriginTransformation: any
  updateSensorPositions: any
}

const ConfigSensorReferenceFrameDialog = (props: ConfigSensorReferenceFrameDialogProps) => {
  const [isInputValid, setIsInputValid] = useState<boolean>(true)
  const fieldWidth = 360
  const fieldHeight = 45
  const defaultValues = {
    xAxisTransformation: 0,
    yAxisTransformation: 0,
    zAxisTransformation: 0
  }

  const dispatch = useAppDispatch()

  const handleSelectOptionChange = (e: any, frame: ReferenceFrame, setFrame: any, axis: string) => {
    frame[axis as keyof ReferenceFrame] = e.target.value as number
    setFrame({
      xAxisTransformation: frame.xAxisTransformation, 
      yAxisTransformation: frame.yAxisTransformation, 
      zAxisTransformation: frame.zAxisTransformation
    })
  }

  const handleTextFieldChange = (e: any, data: any, setData: any, axis: string) => {
    data[axis as keyof object] = parseFloat(e.target.value) as number
    setData({
      xAxisTransformation: data.xAxisTransformation, 
      yAxisTransformation: data.yAxisTransformation, 
      zAxisTransformation: data.zAxisTransformation
    })
    setIsInputValid(validateInputs())
  }

  const validateInputs = () => {
    /*
     *  Check whether the input selection is valid.
     *  The textfield is already limited to numbers only, so the only thing
     *  that needs to be checked is that it is not empty.
     */
    if (props.originTransformation.xAxisTransformation === "") {
      return false
    } else if (props.originTransformation.yAxisTransformation === "") {
      return false
    } else if(props.originTransformation.zAxisTransformation === "") {
      return false
    }
    return true
  }

  const {instance, accounts} = useMsal()
  const handleDownloadFile = async() =>{
    const fileName = "Reference_Frame_Documentation.pdf"
    let url = `${config.url.API_URL}${VEHICLE_PROGRAMS_CONSTANTS.itemDetails.endpoints.getMoreInfoDownload}`
    const idTok = await requestIdToken(instance, accounts)
    fetch(url, {
      method: "POST",
      headers: {
        'Content-Type': "application/json",
        'Authorization': `Bearer ${idTok}`
      },
    }).then((res) => {
      if (res.status === 401)
        instance.loginRedirect(loginRequest).catch(e => {
          console.log(e)
        }
      )
      res.blob().then((blob : any) => {
        const fileURL = window.URL.createObjectURL(blob)
        let alink = document.createElement('a')
        alink.href = fileURL;
        alink.download = fileName
        alink.click();
      })
    }).catch((e) => {
      console.log(e)
    })
  }

  const handleReset = () => {
    props.setGlobalReferenceFrame({
      xAxisTransformation: 0,
      yAxisTransformation: 0,
      zAxisTransformation: 0
    })
    props.setLocalReferenceFrame({
      xAxisTransformation: 0,
      yAxisTransformation: 0,
      zAxisTransformation: 0
    })
    props.setOriginTransformation({
      xAxisTransformation: 0,
      yAxisTransformation: 0,
      zAxisTransformation: 0
    })
  }

  const onClickCancel = () => {
    dispatch(setIsConfigSensorReferenceFrameDialogOpen(false))
  }

  const onClickAccept = () => {
    const reqBody = {
      "vehicleProgramId": props.vehicleProgramId,
      "developmentStageId": props.developmentStageId,
      "globalReferenceFrame": props.globalReferenceFrame,
      "localReferenceFrame": props.localReferenceFrame,
      "originTransformation": props.originTransformation
    }
    props.updateSensorPositions(reqBody)
    if (isEqual(props.globalReferenceFrame, defaultValues) && isEqual(props.localReferenceFrame, defaultValues) && isEqual(props.originTransformation, defaultValues)) {
      dispatch(setCoordinateSystemName("NX"))
    } else {
      dispatch(setCoordinateSystemName("Custom"))
    }
    dispatch(setIsConfigSensorReferenceFrameDialogOpen(false))
  }

  return (
    <Container>
      <ArgosDialogHeader>
        <ArgosFlexbox style={{justifyContent: 'space-between', alignItems: 'center'}}>
          <Header>
            <div
              style={{
                color: 'white',
                paddingLeft: 5,
                fontSize: 14
              }}
            >
              Global Reference Frame Settings
            </div>
          </Header>
          <IconButton onClick={onClickCancel} sx={{color: 'white', padding: 0, fontSize: 'small'}}> 
              <CloseIcon />
          </IconButton>
        </ArgosFlexbox>
      </ArgosDialogHeader>

      <ArgosFlexbox vertical style={{alignItems: "center", padding: 20, width: "100%"}}>
        <ArgosFlexbox
          style={{alignItems: 'center', justifyContent: 'flex-end', width: "100%"}}
        >
          <ArgosButton
            onClick={handleDownloadFile}
          >
            More Info
          </ArgosButton>
          <ArgosButton
            onClick={handleReset}
          >
            Reset
          </ArgosButton>
        </ArgosFlexbox>

        <ArgosFlexbox style={{width: "100%"}}>
          <ArgosFlexbox vertical style={{paddingRight: 10, width: "50%"}}>
            <ArgosFlexbox style={{display: "flex"}}>
              <img src={VectorExample}/>
            </ArgosFlexbox>
            <ArgosFlexbox style={{display: "flex"}}>
              <img src={GlobalFrame}/>
            </ArgosFlexbox>
          </ArgosFlexbox>
        
          <ArgosFlexbox vertical style={{paddingLeft: 10, width: "50%"}}>
          <Typography>
              Axis Transformation
            </Typography>
            <FormControl variant="standard" sx={{width: "100%", height: fieldHeight}}>
              <InputLabel id="global_x_axis">X Axis</InputLabel>
              <ArgosSelect
                required
                label="X Axis"
                type="text"
                value={props.globalReferenceFrame.xAxisTransformation}
                id="global_x_axis"
                onChange={(e) => handleSelectOptionChange(e, props.globalReferenceFrame, props.setGlobalReferenceFrame, "xAxisTransformation")}
                sx={{width: "100%", height: fieldHeight}}
              >
                {axisTransformationOptions.map((element: any, index: number) => <ArgosMenuItem key={index} value={element.value}>{element.label}</ArgosMenuItem>)}
              </ArgosSelect>
            </FormControl>
            <FormControl variant="standard" sx={{width: "100%", height: fieldHeight}}>
              <InputLabel id="global_y_axis">Y Axis</InputLabel>
              <ArgosSelect
                required
                label="Y Axis"
                type="text"
                value={props.globalReferenceFrame.yAxisTransformation}
                id="global_y_axis"
                onChange={(e) => handleSelectOptionChange(e, props.globalReferenceFrame, props.setGlobalReferenceFrame, "yAxisTransformation")}
                sx={{width: "100%", height: fieldHeight}}
              >
                {axisTransformationOptions.map((element: any, index: number) => <ArgosMenuItem key={index} value={element.value}>{element.label}</ArgosMenuItem>)}
              </ArgosSelect>
            </FormControl>
            <FormControl variant="standard" sx={{width: "100%", height: fieldHeight}}>
              <InputLabel id="global_z_axis">Z Axis</InputLabel>
              <ArgosSelect
                required
                label="Z Axis"
                type="text"
                value={props.globalReferenceFrame.zAxisTransformation}
                id="global_z_axis"
                onChange={(e) => handleSelectOptionChange(e, props.globalReferenceFrame, props.setGlobalReferenceFrame, "zAxisTransformation")}
                sx={{width: "100%", height: fieldHeight}}
              >
                {axisTransformationOptions.map((element: any, index: number) => <ArgosMenuItem key={index} value={element.value}>{element.label}</ArgosMenuItem>)}
              </ArgosSelect>
            </FormControl>
            <ArgosFlexbox style={{height: 20}}></ArgosFlexbox>
            <Typography>
              Origin Point Translation
            </Typography>
            <ArgosTextField
              required
              label="X Axis"
              type="number"
              value={props.originTransformation.xAxisTransformation}
              InputProps={{
                endAdornment: <InputAdornment position="end">mm</InputAdornment>
              }}
              id="origin_x_axis"
              onChange={(e) => handleTextFieldChange(e, props.originTransformation, props.setOriginTransformation, "xAxisTransformation")}
              sx={{width: "100%", height: fieldHeight}}
            />
            <ArgosTextField
              required
              label="Y Axis"
              type="number"
              value={props.originTransformation.yAxisTransformation}
              InputProps={{
                endAdornment: <InputAdornment position="end">mm</InputAdornment>
              }}
              id="origin_y_axis"
              onChange={(e) => handleTextFieldChange(e, props.originTransformation, props.setOriginTransformation, "yAxisTransformation")}
              sx={{width: "100%", height: fieldHeight}}
            />
            <ArgosTextField
              required
              label="Z Axis"
              type="number"
              value={props.originTransformation.zAxisTransformation}
              InputProps={{
                endAdornment: <InputAdornment position="end">mm</InputAdornment>
              }}
              id="origin_z_axis"
              onChange={(e) => handleTextFieldChange(e, props.originTransformation, props.setOriginTransformation, "zAxisTransformation")}
              sx={{width: "100%", height: fieldHeight}}
            />
          </ArgosFlexbox>
        </ArgosFlexbox>
      </ArgosFlexbox>

      <div style={{alignSelf: 'center', display: 'flex', gap: 10, marginTop: 10}}>
        <ArgosButton
          onClick={onClickCancel}
        >
          Cancel
        </ArgosButton>
        <ArgosButton
          onClick={onClickAccept}
          disabled={!isInputValid}
        >
          Save
        </ArgosButton>
      </div>
    </Container>
  )
}

export {ConfigSensorReferenceFrameDialog}
